/**
 * 사용 설명서 내용.
 *
 * 글을 컴포넌트에 직접 쓰지 않고 데이터로 분리한 이유:
 *  - 목차·검색·인쇄를 한 곳에서 처리할 수 있다
 *  - 기능이 바뀌었을 때 고칠 곳이 한 군데다
 *  - 글과 화면 구조가 뒤섞이지 않는다
 *
 * 독자는 **안전밸브 계산을 처음 하는 사람**이다. 표준 조항 번호는 근거로
 * 남기되, 문장 자체는 조항을 몰라도 읽히도록 쓴다.
 */

import { API_521_SCENARIOS } from "../data/scenarios";
import { COMMON_PRINCIPLES, RATE_GUIDANCE } from "./rateGuidance";

export type ManualBlock =
  | { readonly kind: "p"; readonly text: string }
  | { readonly kind: "h"; readonly text: string }
  | { readonly kind: "steps"; readonly items: readonly string[] }
  | { readonly kind: "bullets"; readonly items: readonly string[] }
  | {
      readonly kind: "note";
      readonly tone: "info" | "warn" | "tip";
      readonly title?: string;
      readonly text: string;
    }
  | {
      readonly kind: "table";
      readonly head: readonly string[];
      readonly rows: readonly (readonly string[])[];
    }
  | {
      readonly kind: "terms";
      readonly items: readonly { readonly term: string; readonly def: string }[];
    };

export interface ManualSection {
  readonly id: string;
  readonly title: string;
  /** 목차에 붙는 한 줄 설명 */
  readonly summary: string;
  readonly blocks: readonly ManualBlock[];
}

/**
 * 시나리오 17가지 정리 장은 `rateGuidance.ts` 에서 만들어 낸다.
 * 화면의 '입력 자료 안내'와 설명서가 **같은 원본**을 보게 해야
 * 한쪽만 고쳐 어긋나는 일이 없다.
 */
function scenarioSection(): ManualSection {
  const blocks: ManualBlock[] = [
    {
      kind: "p",
      text:
        "과압 시나리오 17가지 중 표준에 계산식이 있는 것은 세 가지뿐입니다. " +
        "나머지는 '무엇이 소요 유량인가'만 정의하고, 그 값은 설계자가 공정 " +
        "자료에서 구해야 합니다. 어떤 자료를 봐야 하는지 아래에 정리했습니다.",
    },
    {
      kind: "p",
      text:
        "케이스 검토 화면에서도 시나리오를 고르면 같은 내용이 '입력 자료 안내'로 " +
        "바로 나옵니다.",
    },
    { kind: "h", text: "모든 시나리오에 공통으로 걸리는 원칙" },
    { kind: "bullets", items: COMMON_PRINCIPLES.map(stripBold) },
    { kind: "h", text: "한눈에 보기" },
    {
      kind: "table",
      head: ["No.", "시나리오", "조항", "소요 유량의 정의"],
      rows: RATE_GUIDANCE.map((g) => {
        const s = API_521_SCENARIOS.find((x) => x.item === g.item);
        return [
          String(g.item),
          s?.conditionKo ?? `항목 ${g.item}`,
          g.clause,
          stripBold(g.vapor ?? g.liquid ?? "—"),
        ];
      }),
    },
  ];

  for (const g of RATE_GUIDANCE) {
    const s = API_521_SCENARIOS.find((x) => x.item === g.item);
    blocks.push({
      kind: "h",
      text: `${g.item}. ${s?.conditionKo ?? ""} (${g.clause})`,
    });
    if (g.vapor) {
      blocks.push({ kind: "p", text: `증기 배출 — ${stripBold(g.vapor)}` });
    }
    if (g.liquid) {
      blocks.push({ kind: "p", text: `액체 배출 — ${stripBold(g.liquid)}` });
    }
    blocks.push(
      { kind: "p", text: "확인할 자료:" },
      { kind: "bullets", items: g.documents.map(stripBold) },
      { kind: "steps", items: g.steps.map(stripBold) },
      {
        kind: "note",
        tone: "warn",
        title: "주의",
        text: g.cautions.map(stripBold).join(" / "),
      },
    );
  }

  return {
    id: "scenarios",
    title: "5. 시나리오 17가지 — 소요 분출용량 정하기",
    summary: "무엇을 보고 유량을 넣나",
    blocks,
  };
}

/** 안내문의 **강조** 표기를 설명서용 평문으로 바꾼다. */
function stripBold(s: string): string {
  return s.replace(/\*\*(.+?)\*\*/g, "$1");
}

export const MANUAL: readonly ManualSection[] = [
  /* ─────────────────────────────────────────────────────── */
  {
    id: "overview",
    title: "1. 이 프로그램이 하는 일",
    summary: "무엇을 계산하고 무엇은 하지 않는지",
    blocks: [
      {
        kind: "p",
        text:
          "안전밸브(PSV)의 크기를 정하는 프로그램입니다. 설비에 과압이 생길 수 있는 " +
          "상황들을 나열하고, 각 상황에서 얼마나 뿜어내야 하는지를 넣으면, 그중 가장 " +
          "큰 밸브를 요구하는 상황(지배 케이스)을 찾아 규격을 확정하고 제출용 계산서를 만듭니다.",
      },
      {
        kind: "h",
        text: "전체 흐름",
      },
      {
        kind: "steps",
        items: [
          "검토할 밸브를 등록한다 (태그·설정압·설치형태)",
          "그 밸브가 막아야 할 과압 상황들을 나열한다 (API 521 의 17가지 중에서)",
          "상황마다 뿜어낼 유량과 유체 조건을 넣는다",
          "프로그램이 상황마다 필요한 오리피스 면적을 계산한다",
          "면적이 가장 큰 상황이 지배 케이스가 된다",
          "그 면적으로 API 526 표준 규격(D~T)을 고른다",
          "계산서를 인쇄하거나 엑셀로 내보낸다",
        ],
      },
      { kind: "h", text: "이 프로그램이 해주는 것" },
      {
        kind: "bullets",
        items: [
          "기체·증기 / 수증기 / 액체 / 2상 유동 네 가지 사이징 계산",
          "임계·아임계 유동 자동 판별, 각종 보정계수 계산",
          "여러 상황을 비교해 지배 케이스 선정",
          "API 526 표준 오리피스 선정 (필요하면 병렬 개수까지)",
          "근거 식과 대입 과정이 담긴 계산서",
        ],
      },
      { kind: "h", text: "이 프로그램이 해주지 않는 것" },
      {
        kind: "bullets",
        items: [
          "각 상황에서 뿜어낼 유량을 계산해주지 않습니다 — 열·물질수지는 설계자가 구해서 넣어야 합니다",
          "유체 물성(분자량·비열비·밀도·점도)을 찾아주지 않습니다 — 공정 자료나 시뮬레이터에서 가져와야 합니다",
          "그래프로만 제공되는 계수(Kb·Kw·Kp)는 계산하지 않습니다 — 제작사 자료에서 읽어 넣어야 합니다",
          "최종 선정을 대신하지 않습니다 — 예비 사이징이며, 제작사의 인증 면적·인증 배출계수로 재확인해야 합니다",
        ],
      },
      {
        kind: "note",
        tone: "warn",
        title: "예비 사이징입니다",
        text:
          "이 프로그램은 표준이 정한 '유효 면적·유효 배출계수'로 계산합니다. 실제 밸브의 " +
          "인증 면적은 이보다 크고 인증 배출계수는 작습니다. 계산서에도 이 문구를 넣어두었으니, " +
          "구매 사양을 확정할 때는 반드시 제작사 자료로 다시 확인하십시오 (API 520 Part I 5.2.4).",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────── */
  {
    id: "quickstart",
    title: "2. 처음 해보기 (10분)",
    summary: "표준서 예제를 그대로 따라 입력해 보기",
    blocks: [
      {
        kind: "p",
        text:
          "API 520 Part I 표준서에 실린 예제를 그대로 넣어봅니다. 답이 맞으면 프로그램을 " +
          "제대로 쓰고 있다는 뜻이고, 동시에 입력 방법을 익히게 됩니다.",
      },
      { kind: "h", text: "예제 조건 (표준서 5.6.3.2)" },
      {
        kind: "table",
        head: ["항목", "값"],
        rows: [
          ["유체", "탄화수소 증기 (프로판·부탄 50:50)"],
          ["소요 분출용량", "24,270 kg/h"],
          ["배출 온도", "348 K (75 ℃)"],
          ["분자량", "51"],
          ["비열비 k", "1.11"],
          ["압축계수 Z", "0.9"],
          ["설정압", "517 kPag"],
          ["배압", "0 kPag (대기 방출)"],
          ["허용 과압", "10 %"],
        ],
      },
      { kind: "h", text: "따라하기" },
      {
        kind: "steps",
        items: [
          "상단에서 [케이스 검토] 탭을 고릅니다.",
          "상단 오른쪽 [압력 단위]가 kPa 인지 확인합니다. 다른 단위로 일하신다면 바꾸셔도 되고, 그러면 아래 압력값도 그 단위로 환산해 넣으시면 됩니다.",
          "'검토 대상 밸브'에서 태그를 PSV-101, 설정압을 517 로 넣습니다. 설치 형태는 '단일 설치'입니다.",
          "'과압 시나리오'에서 첫 행의 시나리오를 '1. 출구 차단'으로 고릅니다.",
          "'과압 상황 설명'에 무슨 상황인지 적습니다. 예: 하류 차단밸브 오조작으로 배출 경로 폐쇄",
          "상황 구분은 '운전', 유체 상태는 '기체·증기'로 둡니다.",
          "소요 분출용량 24270, 배압 0, 온도 348, 분자량 51, 비열비 1.11, 압축계수 0.9 를 넣습니다.",
          "행 아래에 결과가 바로 나옵니다. '소요 면적 3,699 mm²' 가 보이면 맞습니다.",
          "화면 아래 '지배 케이스 및 선정 결과'에서 선정 오리피스가 **P** 로 나오는지 확인합니다.",
        ],
      },
      {
        kind: "note",
        tone: "tip",
        title: "답 맞추기",
        text:
          "표준서는 이 조건에서 소요 면적 3,698 mm², 선정 오리피스 P 라고 적고 있습니다. " +
          "화면에 3,699 mm² · P 가 나오면 제대로 넣으신 겁니다. 1 mm² 차이는 표준서가 " +
          "중간값을 반올림해 실었기 때문입니다.",
      },
      { kind: "h", text: "시나리오를 하나 더 넣어보기" },
      {
        kind: "steps",
        items: [
          "[+ 시나리오 추가]를 누릅니다.",
          "시나리오를 '14. 외부 화재'로 고릅니다. 상황 구분이 자동으로 '화재(21 %)'로 바뀝니다.",
          "소요 분출용량에 26000 을 넣고 나머지는 그대로 둡니다.",
          "결과를 보면 화재 쪽 유량이 더 큰데도 **지배 케이스는 여전히 '1. 출구 차단'** 입니다.",
          "그 이유를 설명하는 안내문이 아래에 나옵니다. 읽어보십시오 — 이 프로그램에서 가장 중요한 개념입니다.",
        ],
      },
    ],
  },

  /* ─────────────────────────────────────────────────────── */
  {
    id: "governing",
    title: "3. 지배 케이스는 왜 '유량'이 아니라 '면적'으로 정하나",
    summary: "가장 자주 오해하는 부분",
    blocks: [
      {
        kind: "p",
        text:
          "많은 실무 자료가 '소요 분출용량이 가장 큰 상황이 지배 케이스'라고 설명합니다. " +
          "조건이 모두 같을 때는 맞는 말이지만, 화재가 섞이면 틀립니다.",
      },
      { kind: "h", text: "허용 과압이 상황마다 다르기 때문입니다" },
      {
        kind: "table",
        head: ["상황", "허용 과압", "근거"],
        rows: [
          ["운전 중 이상, 밸브 1개", "10 %", "API 520 Part I 5.4.2.1.1"],
          ["운전 중 이상, 밸브 여러 개", "16 %", "API 520 Part I 5.4.2"],
          ["외부 화재", "21 %", "API 520 Part I 5.4.3.1.1"],
        ],
      },
      {
        kind: "p",
        text:
          "화재는 21 % 까지 압력이 올라가도 되므로 배출 압력이 높아집니다. 압력이 높으면 " +
          "같은 밸브라도 더 많이 흘릴 수 있습니다. 그래서 화재 유량이 조금 더 크더라도 " +
          "필요한 밸브는 오히려 작을 수 있습니다.",
      },
      { kind: "h", text: "실제 숫자" },
      {
        kind: "table",
        head: ["상황", "소요 유량", "배출압 P1", "필요 면적"],
        rows: [
          ["출구 차단 (과압 10 %)", "24,270 kg/h", "670.0 kPa", "3,699 mm²"],
          ["외부 화재 (과압 21 %)", "26,000 kg/h ←더 큼", "726.9 kPa", "3,653 mm² ←더 작음"],
        ],
      },
      {
        kind: "note",
        tone: "warn",
        title: "유량으로 고르면 밸브를 작게 뽑습니다",
        text:
          "위 예에서 유량만 보고 화재를 지배 케이스로 삼으면 3,653 mm² 로 밸브를 고르게 됩니다. " +
          "그러면 출구 차단 상황에서 용량이 모자랍니다. 그래서 이 프로그램은 항상 면적으로 판정합니다.",
      },
      { kind: "h", text: "면적으로 고르면 다른 상황도 자동으로 커버됩니다" },
      {
        kind: "p",
        text:
          "밸브 용량은 면적 × 배출압에 비례합니다. 각 상황의 필요 면적을 그 상황의 조건에서 " +
          "구한 뒤 가장 큰 것을 고르면, 어느 상황에서도 용량이 모자랄 수 없습니다. " +
          "위 예에서 P 오리피스(4,116 mm²)를 고르면 출구 차단에서 여유 11.3 %, " +
          "화재에서 여유 12.7 % 로 둘 다 충족합니다.",
      },
      {
        kind: "note",
        tone: "info",
        title: "PSM 명세서에는 유량을 적습니다",
        text:
          "판정은 면적으로 하지만, 명세서에 기재하는 값은 소요 분출용량입니다. " +
          "그래서 이 프로그램은 두 값을 항상 함께 보여주고, 유량 1위와 면적 1위가 다르면 " +
          "그 사실과 이유를 화면에 띄웁니다.",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────── */
  {
    id: "inputs",
    title: "4. 입력값은 어디서 구하나",
    summary: "각 항목의 출처",
    blocks: [
      {
        kind: "p",
        text:
          "입력 항목 옆의 물음표(?)에 마우스를 올리면 그 항목의 설명이 나옵니다. " +
          "여기서는 값을 어디서 가져오는지 정리합니다.",
      },
      {
        kind: "table",
        head: ["항목", "어디서", "주의"],
        rows: [
          ["태그·설정압", "P&ID, 안전밸브 명세서", "설정압은 단일 설치에서 설비 MAWP 이하"],
          ["소요 분출용량", "열·물질수지 계산", "표준에 식이 있는 건 3가지뿐 (화재·열팽창·튜브파열)"],
          ["분자량·비열비 k", "공정 자료, 시뮬레이터", "k 는 반드시 이상기체 값"],
          ["압축계수 Z", "시뮬레이터", "모르면 1.0 (보수적)"],
          ["배출 온도", "공정 자료", "배출 시점 기준. 정상운전 온도가 아님"],
          ["배압", "출구 배관 계산 또는 헤더 자료", "중첩배압 + 축적배압"],
          ["비중·점도 (액체)", "공정 자료", "배출 온도 기준"],
          ["비체적·밀도 (2상)", "시뮬레이터 플래시 계산", "두 점 필요 — 입구 조건과 90 % 압력"],
          ["Kb · Kw · Kp", "제작사 자료 / 표준서 그래프", "프로그램이 추정하지 않음"],
          ["MAWP (배관 검토)", "설비 명세서, 압력용기 계산서", "설정압과 혼동 주의"],
        ],
      },
      {
        kind: "note",
        tone: "warn",
        title: "비열비는 이상기체 값을 쓰십시오",
        text:
          "공정 시뮬레이터는 보통 실기체 비열비를 줍니다. 그 값을 그대로 넣으면 " +
          "밸브가 작게 나옵니다. 표준이 명시적으로 금지하는 항목입니다 " +
          "(API 520 Part I 5.6.2.4). k 를 구할 수 없으면 보수적으로 계수 C = 0.0239 를 쓰라고 되어 있습니다.",
      },
      {
        kind: "note",
        tone: "info",
        title: "표준에 유량 계산식이 있는 시나리오",
        text:
          "17가지 중 계산식이 있는 것은 13번(액체 열팽창)·14번(외부 화재)·15번(열교환기 튜브 파열) 세 가지뿐입니다. " +
          "나머지는 표준이 '설계자가 공정 조건을 보고 판단하라'고 합니다. " +
          "11번(내부 폭발)은 아예 안전밸브로 대응하는 대상이 아닙니다.",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────── */
  scenarioSection(),

  /* ─────────────────────────────────────────────────────── */
  {
    id: "piping",
    title: "6. 배관 검토 — 입구 3 % · 배압 · 반력",
    summary: "밸브를 정한 뒤에 반드시 하는 검토",
    blocks: [
      {
        kind: "p",
        text:
          "밸브 크기를 정했다고 끝이 아닙니다. 배관이 잘못되면 밸브가 제 성능을 " +
          "내지 못하거나 아예 망가집니다. 표준(API 520 Part II)이 요구하는 세 가지를 " +
          "이 탭에서 한 번에 봅니다.",
      },
      {
        kind: "table",
        head: ["무엇을", "왜", "기준"],
        rows: [
          [
            "입구 배관 압력손실",
            "손실이 크면 밸브가 열렸다 닫혔다를 반복(채터링)하며 부서집니다",
            "설정압의 3 % 이내 (7.3.4)",
          ],
          [
            "출구 배관 압력손실 = 축적배압",
            "배압이 높으면 밸브가 제때 열리지 않고 용량도 줄어듭니다",
            "형식별로 다름 (5.3)",
          ],
          [
            "대기 방출 반력",
            "배관을 밀어내는 힘입니다. 지지대 설계에 필요합니다",
            "식 (1)",
          ],
        ],
      },
      {
        kind: "note",
        tone: "warn",
        title: "채터링이 왜 위험한가",
        text:
          "밸브가 열리면 입구 배관에서 압력이 떨어집니다. 그 손실이 크면 밸브 " +
          "아래 압력이 설정압 밑으로 내려가 밸브가 닫힙니다. 닫히면 유량이 " +
          "멈추니 압력이 회복되고 다시 열립니다. 이게 초당 수십 번 반복되면 " +
          "시트가 깨지고 밸브가 못 쓰게 됩니다. 3 % 기준은 이걸 막기 위한 것입니다.",
      },
      { kind: "h", text: "따라하기" },
      {
        kind: "steps",
        items: [
          "상단에서 [배관 검토] 탭을 고릅니다.",
          "'검토 조건'에 설정압과 MAWP 를 넣습니다. 이 둘은 다를 수 있습니다 — MAWP 는 설비가 견디는 압력, 설정압은 밸브가 열리는 압력입니다.",
          "유량을 넣습니다. 표준은 '정격 용량'을 쓰라고 합니다 — 선정한 밸브가 실제로 흘릴 수 있는 최대 유량입니다. 오리피스가 소요보다 크면 실제로 더 많이 흐르기 때문입니다 (7.3.7.3).",
          "배출 조건에서의 밀도와 점도를 넣습니다. 점도는 cP 단위입니다 (기체는 보통 0.01~0.03).",
          "'입구 배관'에서 호칭경·스케줄·직관 길이를 넣습니다.",
          "부속을 종류별로 세어 넣습니다. 오른쪽 [+ 부속 추가…]에서 고르고, 표에서 개수를 고칩니다.",
          "바로 아래에 '입구 압력손실 판정'이 적합/부적합으로 나옵니다.",
          "'출구 배관'도 같은 방식으로 넣습니다. 출구 손실은 자동으로 축적배압이 되어 '배압 판정'으로 넘어갑니다.",
          "대기로 직접 뿜는 배관이면 '대기 방출 반력'의 [계산하기]를 켭니다.",
        ],
      },
      {
        kind: "note",
        tone: "tip",
        title: "입구 손실을 줄이는 순서",
        text:
          "3 % 를 넘었다면 이 순서로 고쳐 보십시오. ① 배관을 굵게 — 손실은 " +
          "관경의 5제곱에 반비례하므로 한 치수만 올려도 크게 줄어듭니다. " +
          "② 길이를 짧게 — 밸브를 용기에 가깝게 놓습니다. ③ 엘보를 줄입니다. " +
          "④ 그래도 안 되면 '힘 평형 판정'이 화면에 나타납니다.",
      },
      { kind: "h", text: "부속은 어떻게 세는가" },
      {
        kind: "p",
        text:
          "엘보나 밸브 같은 부속은 '이 부속이 직관 몇 미터에 해당하는가'로 " +
          "바꿔서 계산합니다. 이걸 등가길이라고 합니다. 90° 엘보는 관경의 " +
          "20~50배쯤 되는데, 6인치 배관이면 엘보 하나가 직관 3 m 와 맞먹습니다.",
      },
      {
        kind: "bullets",
        items: [
          "용기에서 배관으로 들어가는 입구 손실은 표준이 반드시 포함하라고 합니다 (7.3.7.2 a) — 기본으로 들어가 있습니다",
          "입구·출구 손실은 길이로 환산하는 개념이 아니라 K 값으로 다룹니다",
          "L/D 값은 API 표준값이 아니라 업계 통용값입니다 — 사내 기준이나 Crane TP-410 이 있으면 그쪽을 쓰십시오",
          "글로브 밸브는 L/D 가 340 으로 매우 큽니다. 안전밸브 입구배관에는 쓰지 않는 것이 원칙입니다",
        ],
      },
      { kind: "h", text: "배압 판정에서 자주 틀리는 것" },
      {
        kind: "note",
        tone: "warn",
        title: "허용 축적배압은 설정압이 아니라 MAWP 기준입니다",
        text:
          "허용 축적배압 = MAWP × (1 + 허용축적%) − 설정압 입니다. '설정압의 " +
          "10 %' 로 외우면 설정압이 MAWP 와 같을 때만 맞습니다. 설정압을 MAWP " +
          "보다 낮게 잡았다면 여유가 그만큼 더 있습니다 (API 520 Part I 식 1).",
      },
      {
        kind: "bullets",
        items: [
          "중첩배압은 밸브가 열리기 전부터 걸려 있는 압력입니다 — 헤더 압력이 여기 해당합니다. 대기 방출이면 0 입니다",
          "축적배압은 배출이 시작된 뒤 배관에서 생기는 압력입니다 — 이 화면이 출구 손실로 자동 계산합니다",
          "벨로우즈형은 축적배압 한계가 아니라 총 배압이 설정압의 50 % 이내인지를 봅니다 (5.3.3.2.1)",
        ],
      },
      { kind: "h", text: "배관 치수에 대하여" },
      {
        kind: "note",
        tone: "info",
        title: "여기 배관 치수는 API 표준이 아닙니다",
        text:
          "호칭경·스케줄 목록은 ASME B36.10M 상용 치수입니다. 배관 규격은 " +
          "안전밸브 표준과 별개이므로, 사내 배관 사양(piping spec)이 다르면 " +
          "각 구간에서 '내경을 직접 넣기'를 켜고 실제 값을 넣으십시오.",
      },
      {
        kind: "note",
        tone: "warn",
        title: "배관을 밸브보다 작게 하지 마십시오",
        text:
          "입구 배관은 밸브 입구 호칭경보다, 출구 배관은 밸브 출구 호칭경보다 " +
          "작으면 안 됩니다 (7.3.2). 입구 손실이 출구보다 크게 나오면 대개 " +
          "이 문제이며, 프로그램이 그때 안내를 띄웁니다.",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────── */
  {
    id: "flare",
    title: "7. 플레어 헤더 — 여러 밸브가 합류할 때의 배압",
    summary: "동시 분출과 헤더 관경",
    blocks: [
      {
        kind: "p",
        text:
          "밸브 여러 개가 하나의 헤더로 모이면, 서로가 서로의 배압을 올립니다. " +
          "혼자 터질 때는 문제없던 밸브가 같이 터질 때는 배압 기준을 넘길 수 있습니다. " +
          "이 탭은 그 상황을 계산합니다.",
      },
      {
        kind: "note",
        tone: "warn",
        title: "배압과 밸브 크기는 서로 물고 있습니다",
        text:
          "밸브를 키우면 유량이 늘고 → 헤더 배압이 오르고 → 배압이 밸브 용량을 깎고 " +
          "→ 다시 밸브를 키워야 합니다. 한 번 계산하고 끝내지 마시고, 여기서 나온 " +
          "배압을 케이스 검토 화면의 배압 칸에 넣어 다시 사이징해 보십시오. " +
          "값이 안정될 때까지 두세 번 오가면 됩니다.",
      },
      { kind: "h", text: "왜 배관 검토 탭과 계산이 다른가" },
      {
        kind: "p",
        text:
          "배관 검토 탭은 밀도가 일정하다고 보고 계산합니다. 밸브 하나의 출구 배관은 " +
          "그래도 됩니다. 하지만 헤더는 팁까지 가는 동안 압력이 크게 떨어져 기체가 " +
          "몇 배로 부풀고, 부푼 만큼 빨라집니다. 그래서 표준은 헤더를 반드시 " +
          "압축성으로 다루라고 합니다 (API 521 5.5.5).",
      },
      {
        kind: "bullets",
        items: [
          "표준은 등온식과 단열식 중 등온식을 권합니다 — 실제는 그 사이에 있고 등온 쪽이 약간 안전합니다",
          "압력을 아는 플레어 팁에서 시작해 거꾸로 거슬러 올라갑니다",
          "관경이 바뀌는 구간마다 따로 계산합니다",
          "⚠ 대기 방출이어도 출구 손실(K=1)을 넣지 않습니다 — 등온식에 이미 들어 있습니다 (5.5.9)",
        ],
      },
      { kind: "h", text: "화면 구성" },
      {
        kind: "table",
        head: ["칸", "무엇이 들어가나", "조합이 바뀌면"],
        rows: [
          ["동시 분출 조합", "어떤 밸브가 얼마를 흘리는가", "이것만 바뀝니다"],
          ["밸브 사양", "분자량·온도·Z·테일파이프", "그대로입니다"],
          ["헤더 구간", "관경·길이·연결", "그대로입니다"],
        ],
      },
      {
        kind: "p",
        text:
          "이렇게 나눈 이유가 있습니다. 정전과 화재는 함께 터지는 밸브가 다를 뿐 " +
          "배관은 같습니다. 유량을 밸브에 두면 조합을 바꿀 때마다 물성까지 다시 " +
          "넣게 됩니다.",
      },
      { kind: "h", text: "따라하기" },
      {
        kind: "steps",
        items: [
          "상단에서 [플레어 헤더] 탭을 고릅니다.",
          "'팁 압력'에 플레어 팁의 압력을 게이지압으로 넣습니다. 이 값은 플레어 벤더 자료에서 얻어야 합니다 — 플랜지 크기만으로는 알 수 없습니다.",
          "'헤더 구간'을 만들고, 구간마다 '흘러가는 곳'을 지정해 망을 그립니다. 마지막 구간은 '플레어 팁 (직결)'을 고릅니다.",
          "'밸브 사양'에서 각 밸브가 어느 구간에 붙는지 고르고 물성과 테일파이프를 넣습니다.",
          "'동시 분출 조합' 표에서 조합마다 각 밸브의 유량을 넣습니다. 그 조합에서 안 터지는 밸브는 0 으로 둡니다.",
          "[+ 조합 추가]로 정전·화재 등을 만들어 비교합니다.",
          "표 오른쪽 '최악 배압'과 '지배 조합'을 봅니다 — 이 값으로 헤더를 정합니다.",
          "[최악 배압을 케이스 검토로 보내기]를 눌러 밸브 사이징에 반영합니다.",
        ],
      },
      {
        kind: "note",
        tone: "warn",
        title: "유량이 가장 큰 조합이 지배 조합이 아닐 수 있습니다",
        text:
          "총 유량이 적어도 그 유량이 한 지선에 몰리면 그쪽 밸브의 배압이 더 " +
          "높아집니다. 실제로 기본 예시에서 총 60,000 kg/h 인 정전보다 " +
          "52,000 kg/h 인 화재가 지선 A 의 밸브들을 지배합니다. " +
          "조합을 하나씩 보는 것만으로는 알 수 없고, 표의 '지배 조합' 열이 " +
          "그래서 필요합니다.",
      },
      { kind: "h", text: "망 그리는 법" },
      {
        kind: "p",
        text:
          "구간마다 '흘러가는 곳'을 고르면 그것이 곧 배관 연결입니다. 여러 " +
          "구간이 같은 구간을 가리키면 거기서 합류합니다.",
      },
      {
        kind: "bullets",
        items: [
          "'플레어 팁 (직결)'을 고른 구간이 팁에 닿습니다 — 그런 구간은 하나뿐이어야 합니다",
          "연결이 서로를 가리키며 돌면(순환) 계산이 거부되고 어느 구간인지 알려줍니다",
          "결과 표는 팁에 가까운 것부터 나오고, 들여쓰기가 팁까지의 거리를 나타냅니다",
          "합류하는 지선들의 출구 압력은 같습니다 — 같은 지점이기 때문입니다",
        ],
      },
      { kind: "h", text: "배압을 밸브 사이징에 반영하기" },
      {
        kind: "p",
        text:
          "[최악 배압을 케이스 검토로 보내기]를 누르면 태그가 같은 밸브를 찾아 " +
          "배압 칸을 채웁니다. 어느 과압 시나리오가 어느 동시 분출 조합에 " +
          "대응하는지는 설계자만 알 수 있으므로, 가장 보수적인 값을 그 밸브의 " +
          "모든 시나리오에 넣습니다.",
      },
      {
        kind: "note",
        tone: "tip",
        title: "값이 안정될 때까지 오가십시오",
        text:
          "배압이 올라가면 밸브 용량이 줄어 밸브를 키워야 하고, 밸브가 커지면 " +
          "유량이 늘어 배압이 또 오릅니다. 배압을 보내고 → 케이스 검토에서 다시 " +
          "사이징하고 → 새 유량을 조합 표에 넣고 → 다시 배압을 보고, 이렇게 " +
          "두세 번 돌면 값이 안정됩니다.",
      },
      { kind: "h", text: "마하수는 왜 보는가" },
      {
        kind: "p",
        text:
          "기체가 빨라지면 소음과 진동이 생기고, 한계에 닿으면 아무리 압력차를 " +
          "키워도 더 흐르지 않습니다(초크). 그러면 배압이 급격히 올라갑니다. " +
          "표에 '임계압'이 함께 나오는데, 구간 출구 압력이 그 값에 가까워지면 " +
          "위험 신호입니다.",
      },
      {
        kind: "note",
        tone: "warn",
        title: "마하수 기준은 표준이 정해주지 않습니다",
        text:
          "API 521 은 값을 정하지 않고 사용자가 기준을 세우도록 합니다 (5.5.3). " +
          "실무에서는 헤더 0.5, 테일파이프 0.7 정도를 쓰는 경우가 많습니다. " +
          "사내 기준이 있으면 그쪽을 쓰고, 화면의 '마하수 상한'을 그 값으로 바꾸십시오. " +
          "참고로 이 화면의 마하수는 등온 음속 기준이라 초크 한계가 1.0 입니다.",
      },
      { kind: "h", text: "부속을 넣는 두 가지 방법" },
      {
        kind: "bullets",
        items: [
          "부속 목록 — 엘보·티를 종류와 개수로 정확히 넣습니다. 상세 설계 단계에서 씁니다",
          "여유율(%) — 직관 길이의 몇 %로 뭉뚱그립니다. 배치가 아직 없을 때 씁니다",
          "표준은 실제 배치 기준으로 부속이 직관 길이의 100 % 이상이 되는 경우가 흔하다고 합니다 (5.5.9)",
          "⚠ 둘 다 넣으면 더해집니다 — 중복해서 넣지 마십시오",
        ],
      },
      { kind: "h", text: "이 탭이 다루지 않는 것" },
      {
        kind: "table",
        head: ["항목", "왜 빠졌나"],
        rows: [
          ["2상 유동 헤더", "액체가 섞이면 계산이 완전히 달라집니다 (5.5.10)"],
          ["녹아웃 드럼·실 드럼", "플레어 계통 설계이며 밸브 사이징으로 되돌아오지 않습니다"],
          ["복사열·스택 높이", "위와 같은 이유입니다. 풍속·지표 조건 등 입력이 다릅니다"],
          ["플레어 팁 선정", "벤더 자료가 필요합니다 (5.5.4, API 537)"],
        ],
      },
    ],
  },

  /* ─────────────────────────────────────────────────────── */
  {
    id: "screens",
    title: "8. 화면 안내",
    summary: "탭별 역할",
    blocks: [
      { kind: "h", text: "케이스 검토" },
      {
        kind: "p",
        text:
          "밸브 하나를 놓고 과압 상황들을 비교해 규격을 확정하는 주 작업 화면입니다. " +
          "상단의 밸브 목록에서 검토할 밸브를 고르고, [+ 밸브 추가]로 늘립니다.",
      },
      {
        kind: "bullets",
        items: [
          "'검토 포함' 체크를 풀면 비교에서 빠지지만 입력값은 남습니다 — 검토했으나 채택하지 않은 상황을 남겨둘 때",
          "각 행 아래에 그 상황의 계산 결과가 바로 나옵니다",
          "지배 케이스인 행은 왼쪽에 색 띠가 붙습니다",
        ],
      },
      { kind: "h", text: "검토 요약" },
      {
        kind: "p",
        text:
          "밸브를 가로로, API 521 의 17가지 시나리오를 세로로 놓은 비교표입니다. " +
          "여러 밸브를 한눈에 보고 빠진 검토가 없는지 확인할 때 씁니다.",
      },
      {
        kind: "bullets",
        items: [
          "N/A 는 그 밸브에 해당 시나리오가 없다는 뜻입니다 — 빈칸이 아니라 N/A 로 표시해 '빠뜨림'과 '해당 없음'을 구분합니다",
          "[소요 분출용량] / [소요 면적] 버튼으로 표시값을 바꿉니다",
          "밸브가 7개를 넘으면 인쇄할 때 다음 장으로 넘어가고, 그 장에도 시나리오 이름 열이 다시 붙습니다",
        ],
      },
      { kind: "h", text: "계산서" },
      {
        kind: "p",
        text:
          "제출용 서식입니다. 검토 조건 → 시나리오 표 → 시나리오별 계산 과정 → 밸브 선정 → 확인 필요 사항 순입니다. " +
          "계산 과정에는 기호식·대입식·결과가 모두 나오므로 검토자가 손으로 따라 계산할 수 있습니다.",
      },
      { kind: "h", text: "배관 검토" },
      {
        kind: "p",
        text:
          "밸브를 정한 뒤 입구·출구 배관을 검토합니다. 자세한 내용은 6장을 " +
          "보십시오. 이 탭은 밸브 목록과 별개로 **한 번에 한 계통**을 봅니다 — " +
          "검토할 계통을 바꾸려면 값을 고쳐 쓰시면 됩니다.",
      },
      { kind: "h", text: "플레어 헤더" },
      {
        kind: "p",
        text:
          "여러 밸브가 공통 헤더로 합류할 때의 배압을 봅니다. 자세한 내용은 " +
          "7장을 보십시오. 배관 검토 탭과 달리 **압축성 유동**으로 계산합니다.",
      },
      { kind: "h", text: "개별 계산기" },
      {
        kind: "p",
        text:
          "유체 상태별 사이징식을 단독으로 확인할 때 씁니다. 초기값이 표준서 예제 조건이라 " +
          "식이 제대로 동작하는지 바로 확인할 수 있습니다. 2상 유동은 입구 조건에 따라 " +
          "두 가지 방법으로 나뉩니다.",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────── */
  {
    id: "results",
    title: "9. 결과 읽는 법",
    summary: "여유율·경고·계산 불가",
    blocks: [
      { kind: "h", text: "여유율" },
      {
        kind: "p",
        text:
          "선정한 표준 오리피스가 필요한 면적보다 얼마나 큰지입니다. 표준 오리피스는 " +
          "D부터 T까지 정해진 값만 있어서 여유율은 항상 0 이상입니다.",
      },
      {
        kind: "note",
        tone: "warn",
        title: "여유율이 너무 크면 오히려 문제입니다",
        text:
          "필요한 것보다 훨씬 큰 밸브를 달면 밸브가 떨리는 현상(채터링)이 생길 수 있습니다. " +
          "여유율이 크게 나오면 한 단계 작은 오리피스로 될 수 없는지, 설정압이나 배관을 " +
          "조정할 여지는 없는지 검토해 보십시오.",
      },
      { kind: "h", text: "경고 (확인 필요 사항)" },
      {
        kind: "p",
        text:
          "계산은 되었지만 설계자가 확인해야 하는 항목입니다. 무시하면 안 됩니다. 자주 나오는 것들:",
      },
      {
        kind: "table",
        head: ["경고", "뜻과 조치"],
        rows: [
          [
            "Kb 가 입력되지 않아 1.0 으로 계산했습니다",
            "밸런스드 벨로우즈를 골랐는데 배압 보정계수를 안 넣었습니다. 제작사 자료에서 읽어 넣으십시오. 안 넣으면 밸브가 작게 나옵니다.",
          ],
          [
            "압축계수 Z 를 1.0 으로 계산했습니다",
            "실제 값이 있으면 넣는 게 정확합니다. 1.0 은 이상기체 가정입니다.",
          ],
          [
            "표준에 유량 계산식이 없습니다",
            "그 시나리오는 열·물질수지로 직접 구해야 한다는 안내입니다.",
          ],
          [
            "일반 안전밸브로 대응하는 대상이 아닙니다",
            "11번 내부 폭발입니다. 상황 자체를 회피하는 설계로 가야 합니다.",
          ],
        ],
      },
      { kind: "h", text: "계산 불가" },
      {
        kind: "p",
        text:
          "물리적으로 성립하지 않는 조건입니다. 대표적인 것은 '배압이 배출 압력 이상' — " +
          "이러면 밸브를 통해 흐르지 않습니다. 배압을 절대압으로 넣어야 할 곳에 게이지압을 " +
          "넣었을 때 자주 나옵니다.",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────── */
  {
    id: "mistakes",
    title: "10. 자주 틀리는 것",
    summary: "특히 조심할 7가지",
    blocks: [
      {
        kind: "table",
        head: ["#", "실수", "왜 위험한가"],
        rows: [
          [
            "1",
            "게이지압 / 절대압을 섞음",
            "액체 계산식은 게이지압, 기체·수증기·2상은 절대압입니다. 화면 라벨에 kPag / kPa abs 로 표시되어 있으니 확인하십시오. 101 kPa 만큼 틀립니다.",
          ],
          [
            "2",
            "실기체 비열비를 넣음",
            "시뮬레이터가 주는 실기체 값을 쓰면 밸브가 작게 나옵니다. 이상기체 값을 쓰십시오.",
          ],
          [
            "3",
            "유량으로 지배 케이스를 정함",
            "화재는 허용 과압이 달라 유량이 커도 필요 면적은 작을 수 있습니다. 3장을 보십시오.",
          ],
          [
            "4",
            "허용 축적배압을 '설정압의 10 %' 로 봄",
            "실제로는 MAWP 기준입니다. 설정압을 MAWP 보다 낮게 잡으면 허용치가 더 큽니다. 설정압 기준으로 보면 과소평가합니다.",
          ],
          [
            "5",
            "입구 배관 손실에서 용기 입구 손실을 빠뜨림",
            "표준이 반드시 포함하라고 명시합니다. 빠뜨리면 3 % 판정을 잘못 통과시킵니다.",
          ],
          [
            "6",
            "반력 계산에 kg/h 를 넣음",
            "반력식의 유량 단위는 kg/s 입니다. kg/h 를 넣으면 3,600배 틀립니다.",
          ],
          [
            "7",
            "2상 계산에서 비체적과 밀도를 바꿔 넣음",
            "입구가 2상이면 비체적(m³/kg), 입구가 과냉 액체면 밀도(kg/m³)입니다. 서로 역수라 바꿔 넣으면 조용히 틀립니다.",
          ],
        ],
      },
      {
        kind: "note",
        tone: "tip",
        title: "확인하는 습관",
        text:
          "계산이 끝나면 계산서 탭에서 '시나리오별 계산 상세'를 펼쳐 대입식을 한 번 읽어보십시오. " +
          "넣은 숫자가 그대로 식에 들어가 있으므로, 자릿수나 단위가 이상하면 바로 눈에 띕니다.",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────── */
  {
    id: "glossary",
    title: "11. 용어 사전",
    summary: "처음 보는 용어 찾기",
    blocks: [
      {
        kind: "terms",
        items: [
          {
            term: "설정압 (Set pressure)",
            def: "밸브가 열리기 시작하는 압력. 게이지압으로 표시합니다. 단일 설치에서는 설비의 MAWP 이하여야 합니다.",
          },
          {
            term: "MAWP (최고허용사용압력)",
            def: "설비가 견디도록 설계된 최고 압력. 설정압과 다릅니다. 설정압은 MAWP 이하로 잡습니다.",
          },
          {
            term: "과압 (Overpressure)",
            def: "밸브가 열린 뒤 설정압을 넘어 더 올라가는 몫. 운전 상황 10 %(단일)·16 %(복수), 화재 21 % 까지 허용됩니다.",
          },
          {
            term: "축적 (Accumulation)",
            def: "MAWP 를 기준으로 압력이 얼마나 올라가도 되는지. 110 % / 116 % / 121 % 로 정해져 있습니다. 과압과 헷갈리기 쉬운데, 과압은 설정압 기준, 축적은 MAWP 기준입니다.",
          },
          {
            term: "배출 압력 P1",
            def: "밸브가 실제로 배출할 때의 입구 압력. 설정압 + 허용 과압 + 대기압으로 계산합니다.",
          },
          {
            term: "중첩배압 (Superimposed backpressure)",
            def: "밸브가 열리기 전부터 출구 쪽에 걸려 있는 압력. 플레어 헤더에 연결되어 있으면 생깁니다.",
          },
          {
            term: "축적배압 (Built-up backpressure)",
            def: "밸브가 열려 유체가 흐르면서 출구 배관에 생기는 압력. 배관이 가늘거나 길면 커집니다.",
          },
          {
            term: "총 배압",
            def: "중첩배압 + 축적배압. 밸브 용량에 영향을 줍니다.",
          },
          {
            term: "블로다운 (Blowdown)",
            def: "열린 밸브가 다시 닫히는 압력과 설정압의 차이. 제작사 자료에서 얻습니다.",
          },
          {
            term: "채터링 (Chattering)",
            def: "밸브가 빠르게 열렸다 닫혔다 반복하며 떨리는 현상. 밸브가 손상됩니다. 입구 압력손실이 크거나 밸브가 지나치게 크면 생깁니다.",
          },
          {
            term: "지배 케이스 (Governing case)",
            def: "검토한 과압 상황 중 가장 큰 밸브를 요구하는 상황. 이 프로그램은 소요 면적으로 판정합니다.",
          },
          {
            term: "유효 오리피스 면적 (Effective area)",
            def: "표준이 정한 명목상의 면적. 실제 밸브의 면적과 다릅니다. 예비 사이징에 씁니다.",
          },
          {
            term: "오리피스 문자 (D ~ T)",
            def: "API 526 이 정한 표준 오리피스 크기. D 가 가장 작고 T 가 가장 큽니다. I 와 O 는 숫자 1·0 과 헷갈려서 없습니다.",
          },
          {
            term: "임계 유동 (Critical flow)",
            def: "기체가 음속에 도달해 하류 압력을 더 낮춰도 유량이 늘지 않는 상태. 배압이 낮으면 여기에 해당합니다.",
          },
          {
            term: "일반형 / 벨로우즈 / 파일럿형",
            def: "밸브 형식. 일반형은 배압에 영향을 받고, 벨로우즈는 벨로우즈로 배압을 상쇄하며, 파일럿형은 리프트가 배압에 영향받지 않습니다.",
          },
          {
            term: "Kd (배출계수)",
            def: "실제 유량과 이론 유량의 비. 예비 사이징에서는 기체·수증기 0.975, 액체 0.65, 2상 0.85 를 씁니다.",
          },
          {
            term: "Kb / Kw (배압 보정계수)",
            def: "배압 때문에 줄어드는 용량을 보정합니다. 밸런스드 벨로우즈에만 적용되며 제작사 자료에서 읽습니다. 기체는 Kb, 액체는 Kw 로 부릅니다.",
          },
          {
            term: "Kc (파열판 조합 보정계수)",
            def: "밸브 상류에 파열판이 있을 때 씁니다. 파열판이 없으면 1.0, 인증값 없는 조합이면 0.9.",
          },
          {
            term: "Kv (점도 보정계수)",
            def: "점성이 큰 액체에서 줄어드는 용량을 보정합니다. 100 cP 이하면 1.0.",
          },
          {
            term: "2상 유동 (Two-phase)",
            def: "액체와 기체가 섞여 배출되는 상태. 액체가 밸브를 지나며 끓는 경우도 포함합니다.",
          },
          {
            term: "오메가 파라미터 ω",
            def: "2상 유동 계산에 쓰는 값. 압력을 10 % 낮췄을 때 부피가 얼마나 늘어나는지를 나타냅니다.",
          },
        ],
      },
    ],
  },

  /* ─────────────────────────────────────────────────────── */
  {
    id: "output",
    title: "12. 계산서와 내보내기",
    summary: "제출용 서류 만들기",
    blocks: [
      { kind: "h", text: "무엇을 실을지 고르기" },
      {
        kind: "p",
        text:
          "계산서 탭 맨 위 '계산서에 실을 항목'에서 밸브 사이징 · 배관 검토 · " +
          "플레어 헤더를 각각 켜고 끌 수 있습니다. 배관과 플레어는 아직 " +
          "검토하지 않았을 수 있으므로 기본은 꺼져 있습니다.",
      },
      {
        kind: "bullets",
        items: [
          "밸브 사이징 — 밸브마다 한 장씩, 시나리오별 계산 과정과 근거 식이 실립니다",
          "배관 검토 — 입구 3 % 판정 · 배압 판정 · 반력을 한 장에",
          "플레어 헤더 — 조합 비교표와 조합별 계산 상세",
          "여기서 고른 항목은 PDF 와 CSV 양쪽에 똑같이 반영됩니다",
        ],
      },
      { kind: "h", text: "PDF 만들기" },
      {
        kind: "steps",
        items: [
          "[계산서] 탭으로 갑니다.",
          "맨 위에서 실을 항목을 고릅니다.",
          "[인쇄 / PDF 저장] 을 누릅니다.",
          "브라우저 인쇄창이 열리면 프린터 목록에서 'PDF로 저장'을 고릅니다.",
          "저장 위치를 정하고 저장합니다.",
        ],
      },
      {
        kind: "note",
        tone: "info",
        text:
          "화면의 탭·버튼·안내문은 인쇄물에 나오지 않습니다. 밸브마다 새 장에서 시작하고, " +
          "표가 장을 넘어가면 머리줄이 반복됩니다. 요약표는 자동으로 가로 방향으로 나갑니다.",
      },
      { kind: "h", text: "엑셀로 내보내기" },
      {
        kind: "p",
        text:
          "[엑셀용 CSV 내보내기]를 누르면 파일이 내려받아집니다. 엑셀에서 바로 열립니다. " +
          "검토자가 숫자를 직접 확인하거나 수정할 때 쓰십시오. 계산서 탭과 요약 탭에서 " +
          "각각 다른 내용이 나갑니다.",
      },
      { kind: "h", text: "저장과 공유" },
      {
        kind: "bullets",
        items: [
          "입력한 내용은 이 브라우저에 자동으로 저장됩니다. 새로고침하거나 껐다 켜도 남아 있습니다.",
          "다른 컴퓨터에서 이어서 하려면 [파일로 내보내기]로 저장한 뒤 그 파일을 옮겨서 [파일 불러오기] 하십시오.",
          "[초기화]를 누르면 처음 예시 상태로 돌아갑니다. 되돌릴 수 없으니 그 전에 파일로 내보내 두십시오.",
        ],
      },
      {
        kind: "note",
        tone: "warn",
        title: "자동 저장은 그 컴퓨터에만 남습니다",
        text:
          "브라우저를 바꾸거나 다른 PC 에서 열면 내용이 없습니다. 시크릿 창이나 사이트 데이터를 " +
          "차단한 환경에서는 자동 저장이 아예 안 되며, 그럴 때는 화면 위에 그렇다고 표시됩니다. " +
          "중요한 검토는 반드시 파일로 내보내 백업하십시오.",
      },
      {
        kind: "note",
        tone: "info",
        title: "입력 데이터는 밖으로 나가지 않습니다",
        text:
          "계산과 저장 모두 이 브라우저 안에서만 일어납니다. 공정 데이터가 외부 서버로 " +
          "전송되지 않으며, 파일도 사용자 PC 안에서 만들어집니다.",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────── */
  {
    id: "units",
    title: "13. 단위",
    summary: "압력 단위 바꾸기",
    blocks: [
      {
        kind: "p",
        text:
          "상단 오른쪽 [압력 단위]에서 kPa · bar · kg/cm² · MPa · psi 중에 고를 수 있습니다. " +
          "단위를 바꾸면 이미 입력한 값도 함께 환산되므로, 물리량은 그대로 유지됩니다.",
      },
      {
        kind: "table",
        head: ["단위", "게이지 표기", "절대 표기", "1 단위 =",],
        rows: [
          ["kPa", "kPag", "kPa abs", "1 kPa"],
          ["bar", "barg", "bar abs", "100 kPa"],
          ["kg/cm²", "kg/cm²g", "kg/cm² abs", "98.0665 kPa"],
          ["MPa", "MPag", "MPa abs", "1,000 kPa"],
          ["psi", "psig", "psia", "6.8948 kPa"],
        ],
      },
      {
        kind: "note",
        tone: "info",
        text:
          "계산은 어떤 단위를 고르든 항상 kPa 로 수행합니다. 표준 계산식이 SI 기준이기 때문입니다. " +
          "계산서에는 입력한 단위와 환산값이 함께 적힙니다. 예: 6 barg (600.0 kPag)",
      },
      {
        kind: "note",
        tone: "warn",
        title: "게이지압과 절대압은 다릅니다",
        text:
          "게이지압은 대기압을 0 으로 보는 압력이고, 절대압은 진공을 0 으로 봅니다. " +
          "차이는 101.325 kPa 입니다. 화면 라벨에 kPag(게이지) 인지 kPa abs(절대) 인지 " +
          "표시되어 있으니 반드시 확인하고 넣으십시오.",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────── */
  {
    id: "standards",
    title: "14. 근거 표준",
    summary: "어느 표준의 어느 판을 따르는가",
    blocks: [
      {
        kind: "table",
        head: ["표준", "판", "이 프로그램에서 쓰는 부분"],
        rows: [
          [
            "API Standard 520 Part I",
            "10판 (2020)",
            "사이징 계산식 전체, 보정계수, 허용 과압, 배압 한계",
          ],
          [
            "API Standard 521",
            "2020년 6월",
            "과압 시나리오 17종 (Table 1)",
          ],
          [
            "API Standard 526",
            "4판 (1995)",
            "표준 오리피스 면적과 밸브 규격",
          ],
          [
            "API Standard 520 Part II",
            "6판 (2015)",
            "배관 압력손실 판정 기준 (입구 3 % 규칙, 반력)",
          ],
        ],
      },
      {
        kind: "note",
        tone: "info",
        title: "API 526 이 1995년판인데 괜찮은가",
        text:
          "오리피스 면적값은 판이 바뀌어도 같습니다. 2020년판 API 520 Part I 본문의 예제 3건" +
          "(L·P·Q 오리피스)과 대조해 mm² 반올림까지 일치함을 확인했습니다.",
      },
      {
        kind: "note",
        tone: "warn",
        title: "배관 압력손실 식은 API 식이 아닙니다",
        text:
          "API 520 Part II 는 '무엇을 손실에 포함할 것인가'만 규정하고 압력강하 계산 자체는 " +
          "일반 유체역학에 맡깁니다. 이 프로그램은 Darcy–Weisbach 식과 Colebrook–White " +
          "마찰계수를 씁니다. 계산서에도 그렇게 표기됩니다. 판정 기준(3 % 등)만 API 조항입니다.",
      },
      {
        kind: "h",
        text: "검증 방법",
      },
      {
        kind: "p",
        text:
          "표준서 본문의 계산 예제를 자동 검증 항목으로 넣어두었습니다. 코드를 고칠 때마다 " +
          "그 예제들을 다시 풀어서 답이 표준서와 맞는지 확인합니다. 기체 2건, 수증기 1건, " +
          "액체 1건, 2상 2건입니다.",
      },
    ],
  },
] as const;

/** 설명서 전체에서 검색어가 들어간 절을 찾는다. */
export function searchManual(query: string): readonly ManualSection[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return MANUAL;

  const hit = (b: ManualBlock): boolean => {
    switch (b.kind) {
      case "p":
      case "h":
        return b.text.toLowerCase().includes(q);
      case "steps":
      case "bullets":
        return b.items.some((t) => t.toLowerCase().includes(q));
      case "note":
        return (
          b.text.toLowerCase().includes(q) ||
          (b.title ?? "").toLowerCase().includes(q)
        );
      case "table":
        return (
          b.head.some((t) => t.toLowerCase().includes(q)) ||
          b.rows.some((r) => r.some((t) => t.toLowerCase().includes(q)))
        );
      case "terms":
        return b.items.some(
          (t) =>
            t.term.toLowerCase().includes(q) || t.def.toLowerCase().includes(q),
        );
    }
  };

  return MANUAL.filter(
    (s) =>
      s.title.toLowerCase().includes(q) ||
      s.summary.toLowerCase().includes(q) ||
      s.blocks.some(hit),
  );
}
